// src/screens/AdminUserDetailScreen.js
import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, ActivityIndicator, Alert, TextInput } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useError } from '../components/ErrorContext';


const COLORS = { GOLD: '#FCD000', BG: '#0b0b0b', CARD: '#101010', TEXT: '#EDEDED', DIM: '#A0A0A0', BORDER: '#1a1a1a', DANGER: '#E74C3C', BLUE: '#3498DB', SUCCESS: '#2ECC71' };
// Fan-out egress estimate. Mirrors settings/restream.egressCentsPerGiB (8.5)
// in functions/modules/restream.js — display only, never a billed number.
const RESTREAM_CENTS_PER_GIB = 8.5;
const GIB = 1024 * 1024 * 1024;
const usd = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
const fmtDate = (isoString) => {
    if (!isoString) return 'N/A'; // Handle null or undefined
    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return 'Invalid Date'; // Check for invalid date
        return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch (e) {
        return 'Invalid Date';
    }
};

// Data-fetching hook for this screen
function useAdminUserData(isAdmin, isLoadingAuth, userId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { showError } = useError(); 

    useEffect(() => {
        if (isLoadingAuth || !isAdmin || !userId) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const getUserDetail = httpsCallable(getFunctions(), 'getAdminUserDetail');
                const result = await getUserDetail({ userId });
                setData(result.data);
            } catch (error) {
                console.error("Error fetching user detail:", error);
                showError("Error", error.message || "Could not fetch user data."); 
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isAdmin, isLoadingAuth, userId, showError]);

    return { data, loading };
}

// --- MODIFICATION: Rewritten InfoRow to be fully type-safe ---
// Helper component for this screen
const InfoRow = ({ label, value }) => {
    let displayValue;

    // 1. Check for null or undefined
    if (value === null || typeof value === 'undefined') {
        displayValue = 'N/A';
    } 
    // 2. Check for numbers (this will correctly handle 0)
    else if (typeof value === 'number') {
        displayValue = value.toString();
    } 
    // 3. Check for strings
    else if (typeof value === 'string') {
        displayValue = value || 'N/A'; // Handle empty string
    } 
    // 4. Check for objects
    else if (typeof value === 'object') {
        // Safely handle an object, preferring 'label' or 'value'
        if (typeof value.label === 'string' && value.label) {
            displayValue = value.label;
        } else if (typeof value.value === 'string' && value.value) {
            displayValue = value.value;
        } else {
            // It's an object we don't know how to render
            displayValue = 'N/A';
        }
    } 
    // 5. Fallback for any other type (boolean, etc.)
    else {
        displayValue = String(value);
    }

    return (
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>{label}</Text>
          <Text style={styles.infoValue}>{displayValue}</Text>
        </View>
    );
};

export default function AdminUserDetailScreen({ navigation }) {
    // --- 1. ALL HOOKS MUST BE CALLED FIRST ---
    const { user, isAdmin, isLoading: isLoadingAuth } = useAdminAuth();
    const route = useRoute();
    const { showError } = useError(); 

    // Safely access route params
    const userId = route.params?.userId;

    const { data, loading: isLoadingData } = useAdminUserData(isAdmin, isLoadingAuth, userId);
    const [txSearch, setTxSearch] = useState('');

    // Grant Crits form. critState overrides the fetched balances after a grant
    // so the display updates without a refetch.
    const [critAmount, setCritAmount] = useState('');
    const [critReason, setCritReason] = useState('');
    const [critGranting, setCritGranting] = useState(false);
    const [critState, setCritState] = useState(null);

    // Simulcast (restream) per-user controls. restreamConfig stays null when the
    // member never configured simulcast — the doc simply does not exist.
    const [restreamConfig, setRestreamConfig] = useState(null);
    const [restreamLoading, setRestreamLoading] = useState(true);
    const [restreamBusy, setRestreamBusy] = useState(false);

    useEffect(() => {
        if (isLoadingAuth || !isAdmin || !userId) {
            setRestreamLoading(false);
            return undefined;
        }
        let cancelled = false;
        const fetchRestream = async () => {
            setRestreamLoading(true);
            try {
                const snap = await getDoc(doc(getFirestore(), 'restreamConfigs', userId));
                if (cancelled) return;
                setRestreamConfig(snap.exists() ? snap.data() : null);
            } catch (err) {
                console.error('Error fetching restreamConfigs doc:', err);
                if (!cancelled) showError('Simulcast', err.message || 'Could not load simulcast configuration.');
            } finally {
                if (!cancelled) setRestreamLoading(false);
            }
        };
        fetchRestream();
        return () => { cancelled = true; };
    }, [isAdmin, isLoadingAuth, userId, showError]);

    // MOVED: useMemo must be called before any conditional returns
    const filteredTransactions = useMemo(() => {
        // Backend field is recentTransactions ('transactions' was a dead read
        // that left this list permanently empty — fixed 2026-07-30).
        const allTransactions = data?.recentTransactions || [];
        if (!txSearch) {
            return allTransactions;
        }
        const searchLower = txSearch.toLowerCase();
        return allTransactions.filter(t => {
            const reason = t.reason?.replace(/_/g, ' ') || t.type || '';
            return reason.toLowerCase().includes(searchLower);
        });
    }, [data?.recentTransactions, txSearch]);


    // --- 2. NON-HOOK LOGIC ---
    const isLoading = isLoadingAuth || isLoadingData;
    const profile = data?.user;
    const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || 'N/A';

    // --- Simulcast derived display values ---
    const restreamDests = Array.isArray(restreamConfig?.destinations) ? restreamConfig.destinations : [];
    const restreamDestSummary = restreamDests.length === 0
        ? 'None'
        : `${restreamDests.length} (${restreamDests.map(d => d?.platform || 'unknown').join(', ')})`;
    const restreamBytes = Number(restreamConfig?.usageMonth?.bytes) || 0;
    const restreamGiB = restreamBytes / GIB;
    const restreamMonthKey = restreamConfig?.usageMonth?.monthKey || '';
    const restreamThisMonth = new Date().toISOString().slice(0, 7);
    const restreamUsageSummary = `${usd(restreamGiB * RESTREAM_CENTS_PER_GIB)} est. ${
        restreamMonthKey && restreamMonthKey !== restreamThisMonth ? `in ${restreamMonthKey}` : 'this month'
    } (${restreamGiB.toFixed(1)} GiB)`;
    const restreamFlags = [];
    if (restreamConfig?.pausedBudget) restreamFlags.push({ label: 'Auto-paused (budget)', color: COLORS.DANGER });
    if (restreamConfig?.disabled) restreamFlags.push({ label: 'Admin-disabled', color: COLORS.DANGER });
    if (restreamConfig?.budgetOverride) restreamFlags.push({ label: 'Budget override active', color: COLORS.GOLD });


    // --- 3. HELPER FUNCTIONS ---
    const onAction = (action) => {
        Alert.alert("Action", `This is where the "${action}" logic would run.`);
    };

    // Beta access grant/revoke
    const handleToggleBetaAccess = () => {
        const hasBeta = profile?.betaAccess === true;
        const action = hasBeta ? 'Revoke' : 'Grant';

        Alert.alert(
            `${action} Beta Access`,
            `Are you sure you want to ${action.toLowerCase()} beta access for ${profile?.userName || 'this user'}?`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: action,
                    style: hasBeta ? 'destructive' : 'default',
                    onPress: async () => {
                        try {
                            const fnName = hasBeta ? 'adminRevokeBetaAccess' : 'adminGrantBetaAccess';
                            const fn = httpsCallable(getFunctions(), fnName);
                            await fn({ userId });
                            Alert.alert('Success', `Beta access ${hasBeta ? 'revoked' : 'granted'}.`);
                            // Refresh data by toggling the local profile
                            if (data?.user) {
                                data.user.betaAccess = !hasBeta;
                                data.user.inviteCode = hasBeta ? data.user.inviteCode : 'ADMIN_GRANTED';
                            }
                        } catch (err) {
                            console.error('Beta access toggle error:', err);
                            Alert.alert('Error', `Failed to ${action.toLowerCase()} beta access.`);
                        }
                    },
                },
            ]
        );
    };

    // Grant (positive) or claw back (negative) Crits via the ledger-backed
    // adminGrantCrits callable — never a raw balance write, so the earned/
    // purchased buckets and critLedger stay consistent.
    const handleGrantCrits = async () => {
        const delta = Math.trunc(Number(critAmount));
        if (!delta) {
            showError('Invalid amount', 'Enter a non-zero whole number of Crits (negative claws back).');
            return;
        }
        setCritGranting(true);
        try {
            const grantFn = httpsCallable(getFunctions(), 'adminGrantCrits');
            const result = await grantFn({
                userId,
                amount: delta,
                reason: critReason.trim() || 'admin_adjustment',
            });
            const r = result.data || {};
            setCritState({ total: r.next, earned: r.earnedAfter, purchased: r.purchasedAfter });
            setCritAmount('');
            setCritReason('');
            showError('Crits updated', `${delta > 0 ? 'Granted' : 'Clawed back'} ${Math.abs(delta)} Crits. New balance: ${r.next}.`);
        } catch (err) {
            console.error('adminGrantCrits error:', err);
            showError('Grant failed', err.message || 'Could not update Crits.');
        } finally {
            setCritGranting(false);
        }
    };

    // Simulcast admin controls. Both buttons hit the same callable
    // (adminSetRestreamUserControls) and patch local state on success so the
    // card updates without a refetch.
    const setRestreamControls = async (patch, successMessage) => {
        setRestreamBusy(true);
        try {
            const fn = httpsCallable(getFunctions(), 'adminSetRestreamUserControls');
            await fn({ userId, ...patch });
            setRestreamConfig(prev => {
                const next = { ...(prev || {}), ...patch };
                // Server clears the auto-pause when an override is granted.
                if (patch.budgetOverride === true) next.pausedBudget = false;
                return next;
            });
            showError('Simulcast updated', successMessage);
        } catch (err) {
            console.error('adminSetRestreamUserControls error:', err);
            showError('Simulcast update failed', err.message || 'Could not update simulcast controls.');
        } finally {
            setRestreamBusy(false);
        }
    };

    const handleToggleRestreamDisabled = () => {
        const isDisabled = restreamConfig?.disabled === true;
        const who = profile?.userName || 'this user';
        const message = isDisabled
            ? `Re-enable simulcast for ${who}?\n\nTheir saved destinations start receiving their stream again on the next sync.`
            : `Disable simulcast for ${who}?\n\nAny running fan-out stops on the next sync and they get a bell notification. Their saved destinations are kept, and their CritCash stream is unaffected.`;
        const ok = typeof window !== 'undefined' ? window.confirm(message) : true;
        if (!ok) return;
        setRestreamControls(
            { disabled: !isDisabled },
            isDisabled ? `Simulcast re-enabled for ${who}.` : `Simulcast disabled for ${who}.`
        );
    };

    const handleToggleRestreamBudgetOverride = () => {
        const hasOverride = restreamConfig?.budgetOverride === true;
        const who = profile?.userName || 'this user';
        const message = hasOverride
            ? `Remove the budget override for ${who}?\n\nThey go back to being auto-paused once their fan-out spend crosses the threshold.`
            : `Grant a budget override to ${who}?\n\nAn override stops auto-pausing this creator at the spend threshold. CritCash keeps paying for their fan-out bandwidth with no ceiling, and any current auto-pause is lifted.`;
        const ok = typeof window !== 'undefined' ? window.confirm(message) : true;
        if (!ok) return;
        setRestreamControls(
            { budgetOverride: !hasOverride },
            hasOverride ? `Budget override removed for ${who}.` : `Budget override granted to ${who}.`
        );
    };

    const onTransactionPress = (tx) => {
        if (!tx.eventId || !tx.eventPath) {
            showError("No Event Data", "This transaction is not linked to a specific event.");
            return;
        }

        const collectionName = tx.eventPath.split('/')[0];
        if (collectionName !== 'tournaments' && collectionName !== 'matches') {
            showError("Invalid Event Path", `Cannot navigate to path: ${tx.eventPath}`);
            return;
        }

        navigation.navigate('EventDetailScreen', {
            id: tx.eventId,
            collection: collectionName,
        });
    };

    
    // --- 4. EARLY RETURNS (NOW SAFE) ---
    if (isLoadingAuth || !user) {
        return <SafeAreaView style={styles.safeArea}><View style={styles.centerWrap}><ActivityIndicator color={COLORS.GOLD} /></View></SafeAreaView>;
    }
    if (!isAdmin) {
        return (
            <SafeAreaView style={styles.safeArea}>
                <View style={styles.centerWrap}>
                    <Text style={styles.deniedText}>Access Denied</Text>
                    <TouchableOpacity onPress={() => navigation.navigate('Lobby')}><Text style={styles.backLink}>Go to Lobby</Text></TouchableOpacity>
                </View>
            </SafeAreaView>
        );
    }

    
    // --- 5. MAIN RENDER ---
    return (
        <SafeAreaView style={styles.safeArea}>
            <Header navigation={navigation} />
            <ScrollView contentContainerStyle={styles.container}>
                <View style={styles.header}>
                    <Text style={styles.title}>User Profile</Text>
                    <Text style={styles.subtitle}>{profile?.userName || '...'} / {profile?.email || '...'}</Text>
                </View>

                <TouchableOpacity style={styles.backButton} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
                    <Text style={styles.backButtonText}>← Back to Manage Users</Text>
                </TouchableOpacity>

                {isLoading ? <ActivityIndicator color={COLORS.GOLD} /> : !profile ? <Text style={styles.empty}>User data not found.</Text> : (
                    <>
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Admin Actions</Text>
                            <View style={styles.actionGrid}>
                                <TouchableOpacity style={styles.actionButton} onPress={() => onAction('Process Withdrawal')}>
                                    <Text style={styles.actionButtonText}>Process Withdrawal</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.actionButton} onPress={() => onAction('Adjust Balance')}>
                                    <Text style={styles.actionButtonText}>Adjust Balance</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.actionButton} onPress={() => onAction('Change Role')}>
                                    <Text style={styles.actionButtonText}>Change Role</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[
                                        styles.actionButton,
                                        { backgroundColor: profile?.betaAccess ? COLORS.DANGER : COLORS.SUCCESS }
                                    ]}
                                    onPress={handleToggleBetaAccess}
                                >
                                    <Text style={styles.actionButtonText}>
                                        {profile?.betaAccess ? 'Revoke Beta' : 'Grant Beta'}
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Crits</Text>
                            <View style={styles.card}>
                                <InfoRow label="Crit Balance" value={critState?.total ?? profile.critBalance ?? 0} />
                                <InfoRow label="Earned (comp-driving)" value={critState?.earned ?? profile.earnedCritBalance ?? 0} />
                                <InfoRow label="Granted / Purchased" value={critState?.purchased ?? profile.purchasedCritBalance ?? 0} />
                                <View style={styles.grantRow}>
                                    <TextInput
                                        style={[styles.grantInput, styles.grantAmountInput]}
                                        placeholder="+/- amount"
                                        placeholderTextColor={COLORS.DIM}
                                        keyboardType="numbers-and-punctuation"
                                        value={critAmount}
                                        onChangeText={setCritAmount}
                                    />
                                    <TextInput
                                        style={[styles.grantInput, styles.grantReasonInput]}
                                        placeholder="Reason (goes in the ledger)"
                                        placeholderTextColor={COLORS.DIM}
                                        value={critReason}
                                        onChangeText={setCritReason}
                                        autoCapitalize="none"
                                    />
                                    <TouchableOpacity
                                        style={[styles.actionButton, styles.grantButton, critGranting && { opacity: 0.5 }]}
                                        onPress={handleGrantCrits}
                                        disabled={critGranting}
                                    >
                                        {critGranting
                                            ? <ActivityIndicator color="#fff" size="small" />
                                            : <Text style={styles.actionButtonText}>Grant Crits</Text>}
                                    </TouchableOpacity>
                                </View>
                                <Text style={styles.grantHint}>
                                    Positive grants, negative claws back. Lands in the granted/purchased bucket (never inflates comp share) and writes a critLedger entry.
                                </Text>
                            </View>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Simulcast</Text>
                            <View style={styles.card}>
                                {restreamLoading ? (
                                    <View style={styles.restreamLoadingWrap}><ActivityIndicator color={COLORS.GOLD} /></View>
                                ) : !restreamConfig ? (
                                    <Text style={styles.empty}>No simulcast configuration.</Text>
                                ) : (
                                    <>
                                        <InfoRow label="Member toggle (enabled)" value={restreamConfig.enabled ? 'Yes' : 'No'} />
                                        <InfoRow label="Destinations" value={restreamDestSummary} />
                                        <InfoRow label="Fan-out usage" value={restreamUsageSummary} />
                                        <View style={styles.flagRow}>
                                            {restreamFlags.length === 0
                                                ? <Text style={styles.flagNone}>No status flags.</Text>
                                                : restreamFlags.map(f => (
                                                    <Text key={f.label} style={[styles.flagPill, { color: f.color, borderColor: f.color }]}>
                                                        {f.label}
                                                    </Text>
                                                ))}
                                        </View>
                                    </>
                                )}
                                <View style={styles.restreamActions}>
                                    <TouchableOpacity
                                        style={[
                                            styles.actionButton,
                                            { backgroundColor: restreamConfig?.disabled ? COLORS.SUCCESS : COLORS.DANGER },
                                            restreamBusy && { opacity: 0.5 },
                                        ]}
                                        onPress={handleToggleRestreamDisabled}
                                        disabled={restreamBusy}
                                    >
                                        <Text style={styles.actionButtonText}>
                                            {restreamConfig?.disabled ? 'Re-enable simulcast' : 'Disable simulcast'}
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[
                                            styles.actionButton,
                                            { backgroundColor: restreamConfig?.budgetOverride ? COLORS.DIM : COLORS.BLUE },
                                            restreamBusy && { opacity: 0.5 },
                                        ]}
                                        onPress={handleToggleRestreamBudgetOverride}
                                        disabled={restreamBusy}
                                    >
                                        <Text style={styles.actionButtonText}>
                                            {restreamConfig?.budgetOverride ? 'Remove budget override' : 'Grant budget override'}
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                                <Text style={styles.grantHint}>
                                    Disable is the admin kill switch and notifies the member. A budget override stops auto-pausing this
                                    creator once their fan-out spend crosses the ops threshold, so the cost keeps running. Usage is
                                    estimated at {`$${(RESTREAM_CENTS_PER_GIB / 100).toFixed(3)}`} per GiB of egress, display only.
                                </Text>
                            </View>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>User Details</Text>
                            <View style={styles.card}>
                                <InfoRow label="Username" value={profile.userName} />
                                <InfoRow label="Email" value={profile.email} />
                                <InfoRow label="Full Name" value={fullName} />
                                <InfoRow label="Wallet" value={usd(profile.balanceCents)} />
                                <InfoRow label="Role" value={profile.role} />
                                <InfoRow label="Phone" value={profile.phone} />
                                <InfoRow label="DOB" value={profile.dob} />
                                <InfoRow label="Joined" value={fmtDate(profile.createdAt)} />
                                <InfoRow label="Beta Access" value={profile.betaAccess ? 'Yes' : 'No'} />
                                <InfoRow label="Invite Code" value={profile.inviteCode || 'None'} />
                            </View>
                        </View>

                        <View style={styles.section}>
                            <View style={styles.sectionHeaderWithSearch}>
                                <Text style={styles.sectionTitle}>Recent Transactions (Last 20)</Text>
                                <TextInput
                                    style={styles.searchInput}
                                    placeholder="Filter..."
                                    placeholderTextColor={COLORS.DIM}
                                    value={txSearch}
                                    onChangeText={setTxSearch}
                                    autoCapitalize="none"
                                    autoCorrect={false}
                                />
                            </View>
                            {filteredTransactions.length === 0 ? <Text style={styles.empty}>No transactions found.</Text> : (
                                <ScrollView style={{ maxHeight: 400 }}>
                                    <View style={styles.card}>
                                        {filteredTransactions.map(t => (
                                            <TouchableOpacity key={t.id} style={styles.txRow} onPress={() => onTransactionPress(t)}>
                                                <View>
                                                    <Text style={[styles.txReason, t.type === 'debit' ? styles.txDebit : styles.txCredit]}>
                                                        {t.reason?.replace(/_/g, ' ') || t.type}
                                                    </Text>
                                                    <Text style={styles.txDate}>{fmtDate(t.at)}</Text>
                                                </View>
                                                <View style={{alignItems: 'flex-end'}}>
                                                    <Text style={[styles.txAmount, t.type === 'debit' ? styles.txDebit : styles.txCredit]}>
                                                        {t.type === 'debit' ? '-' : '+'}{usd(t.amountCents)}
                                                    </Text>
                                                    {t.eventId && <Text style={styles.txEventId}>View Event →</Text>}
                                                </View>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </ScrollView>
                            )}
                        </View>
                    </>
                )}
            </ScrollView>
            <Footer navigation={navigation} />
            <NavBar navigation={navigation} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: COLORS.BG },
    centerWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
    deniedText: { color: COLORS.DANGER, fontSize: 18, fontWeight: 'bold', textAlign: 'center' },
    backLink: { color: COLORS.DIM, marginTop: 16, textDecorationLine: 'underline' },
    container: { padding: 16, paddingBottom: 120 },
    header: { alignItems: 'center', marginBottom: 16 },
    title: { fontSize: 28, fontWeight: '900', color: COLORS.GOLD, textAlign: 'center' },
    subtitle: { color: COLORS.DIM, fontSize: 14, marginTop: 4, textAlign: 'center' },
    backButton: {
        marginBottom: 20,
        alignSelf: 'flex-start',
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 6,
        backgroundColor: COLORS.CARD,
        borderWidth: 1,
        borderColor: COLORS.BORDER,
    },
    backButtonText: { color: COLORS.GOLD, fontWeight: '700', fontSize: 14 },
    section: { width: '100%', marginBottom: 24 },
    sectionTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: 'bold', marginBottom: 12, borderLeftWidth: 3, borderLeftColor: COLORS.GOLD, paddingLeft: 8 },
    card: { backgroundColor: COLORS.CARD, borderRadius: 8, borderWidth: 1, borderColor: COLORS.BORDER, overflow: 'hidden' },
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: COLORS.BORDER },
    infoLabel: { color: COLORS.DIM, fontSize: 14, fontWeight: '600' },
    infoValue: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700', flexShrink: 1, textAlign: 'right' },
    empty: { color: COLORS.DIM, fontStyle: 'italic', fontSize: 13, textAlign: 'center', padding: 10 },
    
    actionGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, },
    actionButton: { flex: 1, minWidth: '45%', backgroundColor: COLORS.BLUE, padding: 12, borderRadius: 8, alignItems: 'center' },
    actionButtonText: { color: '#fff', fontWeight: '700' },

    grantRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, padding: 14, alignItems: 'center' },
    grantInput: { backgroundColor: COLORS.BG, borderColor: COLORS.BORDER, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, color: COLORS.TEXT, fontSize: 14 },
    grantAmountInput: { width: 110 },
    grantReasonInput: { flex: 1, minWidth: 160 },
    grantButton: { flex: 0, minWidth: 120, backgroundColor: COLORS.SUCCESS },
    grantHint: { color: COLORS.DIM, fontSize: 12, paddingHorizontal: 14, paddingBottom: 12, fontStyle: 'italic' },

    restreamLoadingWrap: { padding: 16, alignItems: 'center' },
    restreamActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, padding: 14 },
    flagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingHorizontal: 14, paddingTop: 12 },
    flagPill: { fontSize: 12, fontWeight: '700', borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4 },
    flagNone: { color: COLORS.DIM, fontSize: 12, fontStyle: 'italic' },

    sectionHeaderWithSearch: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    searchInput: {
        backgroundColor: COLORS.CARD,
        borderColor: COLORS.BORDER,
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 6,
        color: COLORS.TEXT,
        fontSize: 14,
        width: '40%',
    },

    txRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: COLORS.BORDER },
    txReason: { color: COLORS.TEXT, fontWeight: '700', textTransform: 'capitalize' },
    txDate: { color: COLORS.DIM, fontSize: 12, marginTop: 2 },
    txAmount: { fontSize: 16, fontWeight: '900' },
    txDebit: { color: '#E74C3C' },
    txCredit: { color: '#2ECC71' },
    txEventId: { color: COLORS.BLUE, fontSize: 11, fontWeight: '600', marginTop: 2 },
});